import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  getCountFromServer,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Product, Review } from '@/data/products';
import { deleteReviewImages } from './imageUploadService';

// Global brands cache to ensure we only fetch once
let brandsCache: string[] | null = null;
let brandsCachePromise: Promise<string[]> | null = null;

// Pagination result type
export interface PaginatedProductsResult {
  products: Product[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total?: number;
}

// Search result type
export interface SearchProductsResult {
  products: Product[];
  total: number;
}

// Product category type
export type ProductCategory = 'all' | 'best-sellers' | 'on-sale' | 'new';

/**
 * Helper function to process product data consistently
 */
const processProductData = (doc: QueryDocumentSnapshot<DocumentData>): Product => {
  const data = doc.data();
  return {
    id: doc.id,
    name: data.name || '',
    description: data.description || '',
    price: data.price || 0,
    onSalePrice: data.onSalePrice || null,
    image: data.image || (data.images && data.images[0]) || '/api/placeholder/300/300',
    images: data.images || ['/api/placeholder/300/300'],
    category: data.category || 'Uncategorized',
    brand: data.brand || null,
    tags: data.tags || [],
    isNew: data.isNew || false,
    isBestSeller: data.isBestSeller || false,
    onSale: data.onSale || false,
    disabled: data.disabled || false,
    stockQuantity: data.stockQuantity || 0,
    lowStockThreshold: data.lowStockThreshold || 10,
    netWeight: data.netWeight || null,
    weightUnit: data.weightUnit || null,
    listingQuantity: data.listingQuantity || 1,
    availableShippingOptions: data.availableShippingOptions || [],
    seoTitle: data.seoTitle || null,
    seoDescription: data.seoDescription || null,
    salesCount: data.salesCount || 0,
    salesThisMonth: data.salesThisMonth || 0,
    revenueThisMonth: data.revenueThisMonth || 0,
    lastStatsUpdate: data.lastStatsUpdate || null,
    variations: data.variations || [],
    parentVariationGroup: data.parentVariationGroup || null,
    reviews: data.reviews || [],
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  } as Product;
};

/**
 * Get the total count of products for a given category
 */
export const getProductCount = async (category: ProductCategory = 'all'): Promise<number> => {
  try {
    const productsCollection = collection(db, 'products');
    let countQuery;
    
    switch (category) {
      case 'best-sellers':
        countQuery = query(
          productsCollection, 
          where('isBestSeller', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0)
        );
        break;
      case 'on-sale':
        countQuery = query(
          productsCollection, 
          where('onSale', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0)
        );
        break;
      case 'new':
        countQuery = query(
          productsCollection, 
          where('isNew', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0)
        );
        break;
      case 'all':
      default:
        countQuery = query(
          productsCollection,
          where('disabled', '!=', true),
          where('price', '>', 0)
        );
        break;
    }
    
    const snapshot = await getCountFromServer(countQuery);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting product count:', error);
    throw new Error('Failed to get product count');
  }
};

/**
 * Fetch cursors for intermediate pages (used for non-sequential pagination)
 */
export const fetchPageCursors = async (
  targetPage: number,
  pageSize: number = 20,
  category: ProductCategory = 'all'
): Promise<Map<number, QueryDocumentSnapshot<DocumentData> | null>> => {
  try {
    const cursors = new Map<number, QueryDocumentSnapshot<DocumentData> | null>();
    const productsCollection = collection(db, 'products');
    
    // Build base query based on category
    let baseQuery;
    switch (category) {
      case 'best-sellers':
        baseQuery = query(
          productsCollection,
          where('isBestSeller', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('salesCount', 'desc'),
          orderBy('__name__')
        );
        break;
      case 'on-sale':
        baseQuery = query(
          productsCollection,
          where('onSale', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('__name__')
        );
        break;
      case 'new':
        baseQuery = query(
          productsCollection,
          where('isNew', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('__name__')
        );
        break;
      case 'all':
      default:
        baseQuery = query(
          productsCollection,
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('__name__')
        );
        break;
    }

    // Fetch documents up to the target page to get all intermediate cursors
    const totalDocsNeeded = (targetPage - 1) * pageSize;
    const fetchQuery = query(baseQuery, limit(totalDocsNeeded));
    
    if (totalDocsNeeded > 0) {
      const snapshot = await getDocs(fetchQuery);
      const docs = snapshot.docs;
      
      // Store cursors for each page
      for (let page = 1; page < targetPage; page++) {
        const docIndex = page * pageSize - 1; // Last doc of the page
        if (docIndex < docs.length) {
          cursors.set(page, docs[docIndex]);
        }
      }
    }
    
    return cursors;
  } catch (error) {
    console.error('Error fetching page cursors:', error);
    throw new Error('Failed to fetch page cursors');
  }
};

/**
 * Fetch products with pagination support
 */
export const fetchProductsPaginated = async (
  pageSize: number = 20,
  category: ProductCategory = 'all',
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null
): Promise<PaginatedProductsResult> => {
  try {
    const productsCollection = collection(db, 'products');
    let productQuery;
    
    // Build base query based on category
    switch (category) {
      case 'best-sellers':
        productQuery = query(
          productsCollection,
          where('isBestSeller', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('salesCount', 'desc'),
          orderBy('__name__') // Add document ID for stable ordering
        );
        break;
      case 'on-sale':
        productQuery = query(
          productsCollection,
          where('onSale', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('__name__') // Simple ordering by document ID
        );
        break;
      case 'new':
        productQuery = query(
          productsCollection,
          where('isNew', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('__name__') // Simple ordering by document ID
        );
        break;
      case 'all':
      default:
        productQuery = query(
          productsCollection,
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('__name__') // Simple ordering by document ID
        );
        break;
    }
    
    // Add pagination
    if (lastDoc) {
      productQuery = query(productQuery, startAfter(lastDoc), limit(pageSize));
    } else {
      productQuery = query(productQuery, limit(pageSize));
    }
    
    const productSnapshot = await getDocs(productQuery);
    const products = productSnapshot.docs.map(processProductData);
    
    // Get the last document for next page
    const newLastDoc = productSnapshot.docs.length > 0 
      ? productSnapshot.docs[productSnapshot.docs.length - 1] 
      : null;
    
    // Check if there are more documents
    const hasMore = productSnapshot.docs.length === pageSize;
    
    return {
      products,
      lastDoc: newLastDoc,
      hasMore
    };
  } catch (error) {
    console.error('Error fetching paginated products:', error);
    throw new Error('Failed to fetch products');
  }
};

/**
 * Fetch ALL products in a category for comprehensive search
 * Used when user performs a search to enable searching across all products
 */
export const fetchAllProductsForSearch = async (
  category: ProductCategory = 'all'
): Promise<SearchProductsResult> => {
  try {
    const productsCollection = collection(db, 'products');
    let searchQuery;
    
    // Build query based on category (same logic as pagination)
    switch (category) {
      case 'best-sellers':
        searchQuery = query(
          productsCollection,
          where('isBestSeller', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('salesCount', 'desc'),
          orderBy('__name__')
        );
        break;
      case 'on-sale':
        searchQuery = query(
          productsCollection,
          where('onSale', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('__name__')
        );
        break;
      case 'new':
        searchQuery = query(
          productsCollection,
          where('isNew', '==', true),
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('__name__')
        );
        break;
      case 'all':
      default:
        // For 'all' category, we might want to limit to prevent excessive costs
        // Fetching all products could be expensive if you have thousands
        searchQuery = query(
          productsCollection,
          where('disabled', '!=', true),
          where('price', '>', 0),
          orderBy('disabled'),
          orderBy('price'),
          orderBy('__name__'),
          limit(1000) // Safety limit to prevent excessive costs
        );
        break;
    }
    
    const productSnapshot = await getDocs(searchQuery);
    const products = productSnapshot.docs.map(processProductData);
    
    
    return {
      products,
      total: products.length
    };
  } catch (error) {
    console.error('Error fetching products for search:', error);
    throw new Error('Failed to fetch products for search');
  }
};

/**
 * Search products with optional filters (brand, category, price range)
 * Used for efficient filtering with Firebase queries
 */
export const searchProducts = async (
  searchQuery: string = '',
  category?: ProductCategory,
  brand?: string,
  minPrice?: number,
  maxPrice?: number
): Promise<SearchProductsResult> => {
  try {
    const productsCollection = collection(db, 'products');
    let productQuery;
    
    // Build base query with category filter
    const filters = [where('disabled', '!=', true)];
    
    // Always exclude products with price 0
    const effectiveMinPrice = minPrice !== undefined ? Math.max(minPrice, 0.01) : 0.01;
    filters.push(where('price', '>=', effectiveMinPrice));
    
    // Add brand filter if specified
    if (brand) {
      filters.push(where('brand', '==', brand));
    }
    
    // Add max price filter if specified
    if (maxPrice !== undefined) {
      filters.push(where('price', '<=', maxPrice));
    }
    
    // Build base query based on category
    switch (category) {
      case 'best-sellers':
        productQuery = query(
          productsCollection,
          ...filters,
          where('isBestSeller', '==', true),
          orderBy('disabled'),
          orderBy('price'), // Add price ordering for price filters
          orderBy('salesCount', 'desc'),
          orderBy('__name__'),
          limit(1000)
        );
        break;
      case 'on-sale':
        productQuery = query(
          productsCollection,
          ...filters,
          where('onSale', '==', true),
          orderBy('disabled'),
          orderBy('price'), // Add price ordering for price filters
          orderBy('__name__'),
          limit(1000)
        );
        break;
      case 'new':
        productQuery = query(
          productsCollection,
          ...filters,
          where('isNew', '==', true),
          orderBy('disabled'),
          orderBy('price'), // Add price ordering for price filters
          orderBy('__name__'),
          limit(1000)
        );
        break;
      case 'all':
      default:
        productQuery = query(
          productsCollection,
          ...filters,
          orderBy('disabled'),
          orderBy('price'), // Add price ordering for price filters
          orderBy('__name__'),
          limit(1000)
        );
        break;
    }
    
    const snapshot = await getDocs(productQuery);
    const allProducts = snapshot.docs.map(processProductData);
    
    // Apply search query filter if provided
    let filteredProducts = allProducts;
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filteredProducts = allProducts.filter(product => 
        product.name.toLowerCase().includes(searchLower) ||
        product.description.toLowerCase().includes(searchLower) ||
        (product.category && product.category.toLowerCase().includes(searchLower)) ||
        (product.brand && product.brand.toLowerCase().includes(searchLower)) ||
        (product.tags && product.tags.some(tag => tag.toLowerCase().includes(searchLower)))
      );
    }
    
    
    return {
      products: filteredProducts,
      total: filteredProducts.length
    };
  } catch (error) {
    console.error('Error searching products:', error);
    throw new Error('Failed to search products');
  }
};

/**
 * Get unique brands from products collection with global caching
 * Cost-effective approach using existing product data
 */
export const getUniqueBrands = async (): Promise<string[]> => {
  // Return cached data if available
  if (brandsCache) {
    return brandsCache;
  }

  // Return existing promise if already fetching
  if (brandsCachePromise) {
    return brandsCachePromise;
  }

  // Create new fetch promise
  brandsCachePromise = (async () => {
    try {
      const productsCollection = collection(db, 'products');
      
      // Firebase limitation: can only use one != filter per query
      // So we'll fetch products that are not disabled and filter for non-null brands client-side
      const brandsQuery = query(
        productsCollection,
        where('disabled', '!=', true),
        orderBy('disabled'),
        orderBy('brand'),
        limit(500) // Reasonable limit for brand extraction
      );
      
      const snapshot = await getDocs(brandsQuery);
      const brands = new Set<string>();
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Filter out null/undefined brands and products with price 0 client-side
        if (data.brand && typeof data.brand === 'string' && data.brand.trim() && (data.price || 0) > 0) {
          brands.add(data.brand.trim());
        }
      });
      
      const sortedBrands = Array.from(brands).sort();
      
      // Cache the result
      brandsCache = sortedBrands;
      return sortedBrands;
    } catch (error) {
      console.error('Error fetching unique brands:', error);
      // Clear the promise so retry is possible
      brandsCachePromise = null;
      throw new Error('Failed to fetch brands from Firebase');
    }
  })();

  return brandsCachePromise;
};

/**
 * Get unique categories from products collection (enabled products only)
 * Mirrors brand extraction with caching for efficiency
 */
let categoriesCache: string[] | null = null;
let categoriesCachePromise: Promise<string[]> | null = null;

export const getUniqueCategories = async (): Promise<string[]> => {
  if (categoriesCache) return categoriesCache;
  if (categoriesCachePromise) return categoriesCachePromise;

  categoriesCachePromise = (async () => {
    try {
      const productsCollection = collection(db, 'products');
      const q = query(
        productsCollection,
        where('disabled', '!=', true),
        orderBy('disabled'),
        limit(1000)
      );
      const snapshot = await getDocs(q);
      const categories = new Set<string>();
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (data.category && typeof data.category === 'string' && data.category.trim() && (data.price || 0) > 0) {
          categories.add(data.category.trim());
        }
      });
      const sorted = Array.from(categories).sort();
      categoriesCache = sorted;
      return sorted;
    } catch (error) {
      categoriesCachePromise = null;
      throw new Error('Failed to fetch categories from Firebase');
    }
  })();

  return categoriesCachePromise;
};

/**
 * Clear brands cache (useful for admin operations or when brands might have changed)
 */
export const clearBrandsCache = (): void => {
  brandsCache = null;
  brandsCachePromise = null;
};

/**
 * Fetch top selling products (for hero section and featured products)
 */
export const fetchTopSellingProducts = async (limitCount: number = 10): Promise<Product[]> => {
  try {
    const productsCollection = collection(db, 'products');
    const fetchLimit = Math.max(limitCount * 3, 15);
    const topProductsQuery = query(
      productsCollection,
      where('disabled', '!=', true),
      orderBy('disabled'),
      orderBy('salesCount', 'desc'),
      orderBy('__name__'), // For stable ordering
      limit(fetchLimit)
    );
    
    const productSnapshot = await getDocs(topProductsQuery);
    const products = productSnapshot.docs.map(processProductData);
    return products
      .filter((p) => (p.price || 0) > 0)
      .slice(0, limitCount);
  } catch (error) {
    console.error('Error fetching top selling products:', error);
    throw new Error('Failed to fetch top selling products');
  }
};

/**
 * Fetch best seller products (fallback when no sales data exists)
 */
export const fetchBestSellerProducts = async (limitCount: number = 10): Promise<Product[]> => {
  try {
    const productsCollection = collection(db, 'products');
    const fetchLimit = Math.max(limitCount * 3, 15);
    const bestSellerQuery = query(
      productsCollection,
      where('isBestSeller', '==', true),
      where('disabled', '!=', true),
      orderBy('disabled'),
      orderBy('__name__'),
      limit(fetchLimit)
    );
    
    const productSnapshot = await getDocs(bestSellerQuery);
    const products = productSnapshot.docs.map(processProductData);
    return products
      .filter((p) => (p.price || 0) > 0)
      .slice(0, limitCount);
  } catch (error) {
    console.error('Error fetching best seller products:', error);
    throw new Error('Failed to fetch best seller products');
  }
};

/**
 * Fetches a single product by ID
 */
export const fetchProductById = async (productId: string): Promise<Product | null> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      return null;
    }
    
    const product = processProductData(productDoc);
    
    // Return null for disabled products or products with price 0 in customer app
    if (product.disabled || product.price <= 0) {
      return null;
    }
    
    return product;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw new Error(`Failed to fetch product with ID: ${productId}`);
  }
};

// Fetch product for rewards (allows $0 price products)
export const fetchProductByIdForRewards = async (productId: string): Promise<Product | null> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      return null;
    }
    
    const product = processProductData(productDoc);
    
    // For rewards, only exclude truly disabled products, allow $0 price
    if (product.disabled) {
      return null;
    }
    
    return product;
  } catch (error) {
    console.error('Error fetching product for reward:', error);
    throw new Error(`Failed to fetch product for reward with ID: ${productId}`);
  }
};

/**
 * Creates a new review for a product
 */
export const addReview = async (
  productId: string,
  userId: string,
  userName: string,
  userAvatar: string,
  content: string,
  rating: number,
  images?: string[]
): Promise<Product> => {
  try {
    // First get the latest version of the product to avoid race conditions
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }
    
    const currentProduct = productDoc.data() as Product;
    const currentReviews = currentProduct.reviews || [];
    
    // Check if user already has a verified review (should edit instead of creating new)
    const existingVerifiedReview = currentReviews.find(
      review => review.userId === userId && review.status === 'verified'
    );
    
    if (existingVerifiedReview) {
      throw new Error('You already have a verified review for this product. Please edit your existing review instead of creating a new one.');
    }
    
    // Check if user already has a pending review (replace it)
    const existingPendingReviewIndex = currentReviews.findIndex(
      review => review.userId === userId && review.status === 'pending'
    );
    
    // Clean up images from existing pending review if being replaced
    if (existingPendingReviewIndex >= 0) {
      const existingPendingReview = currentReviews[existingPendingReviewIndex];
      if (existingPendingReview.images && existingPendingReview.images.length > 0) {
        try {
          await deleteReviewImages(existingPendingReview.images);
          } catch (cleanupError) {
          console.warn('Failed to cleanup images from overwritten pending review:', cleanupError);
          // Don't block the review creation if image cleanup fails
        }
      }
    }
    
    const newReview: Review = {
      id: Date.now(),
      name: userName,
      date: new Date().toISOString(),
      content,
      rating,
      avatar: userAvatar,
      userId,
      status: 'pending' as const,
      images: images || []
    };
    
    let updatedReviews;
    
    if (existingPendingReviewIndex >= 0) {
      // Replace existing pending review
      updatedReviews = [...currentReviews];
      updatedReviews[existingPendingReviewIndex] = newReview;
    } else {
      // Add new review (no existing review found)
      updatedReviews = [newReview, ...currentReviews];
    }
    
    await updateDoc(productRef, {
      reviews: updatedReviews
    });
    
    // Return updated product
    return {
      ...currentProduct,
      id: productId,
      reviews: updatedReviews
    };
  } catch (error) {
    console.error('Error adding review:', error);
    throw new Error('Failed to add review');
  }
};

/**
 * Updates an existing review
 */
export const updateReview = async (
  productId: string,
  reviewId: number,
  content: string,
  rating: number,
  images?: string[]
): Promise<Product> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }
    
    const currentProduct = productDoc.data() as Product;
    const currentReviews = currentProduct.reviews || [];
    
    // Find and update the review
    const updatedReviews = currentReviews.map(review => {
      if (review.id === reviewId) {
        return {
          ...review,
          content,
          rating,
          date: new Date().toISOString(),
          status: 'pending' as const,
          images: images || review.images || []
        };
      }
      return review;
    });
    
    await updateDoc(productRef, {
      reviews: updatedReviews
    });
    
    // Return updated product
    return {
      ...currentProduct,
      id: productId,
      reviews: updatedReviews
    };
  } catch (error) {
    console.error('Error updating review:', error);
    throw new Error('Failed to update review');
  }
};

/**
 * Deletes a review
 */
export const deleteReview = async (
  productId: string,
  reviewId: number
): Promise<Product> => {
  try {
    const productRef = doc(db, 'products', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Product not found');
    }
    
    const currentProduct = productDoc.data() as Product;
    const currentReviews = currentProduct.reviews || [];
    
    // Find the review to delete and clean up its images
    const reviewToDelete = currentReviews.find(review => review.id === reviewId);
    if (reviewToDelete && reviewToDelete.images && reviewToDelete.images.length > 0) {
      try {
        await deleteReviewImages(reviewToDelete.images);
      } catch (cleanupError) {
        console.warn('Failed to cleanup images from deleted review:', cleanupError);
        // Don't block the review deletion if image cleanup fails
      }
    }
    
    // Filter out the review to delete
    const updatedReviews = currentReviews.filter(review => review.id !== reviewId);
    
    await updateDoc(productRef, {
      reviews: updatedReviews
    });
    
    // Return updated product
    return {
      ...currentProduct,
      id: productId,
      reviews: updatedReviews
    };
  } catch (error) {
    console.error('Error deleting review:', error);
    throw new Error('Failed to delete review');
  }
};

/**
 * Gets related products based on category, price similarity, and sales count
 * Prioritizes products from the same category with similar price points (±$2 max)
 */
export const getRelatedProducts = async (
  productId: string,
  limitCount: number = 10
): Promise<Product[]> => {
  try {
    // First, get the current product to understand its category and price
    const currentProduct = await fetchProductById(productId);
    if (!currentProduct) {
      return [];
    }

    const productsCollection = collection(db, 'products');
    let relatedProducts: Product[] = [];

    // Get current product price (use sale price if applicable)
    const currentPrice = currentProduct.onSale && currentProduct.onSalePrice 
      ? currentProduct.onSalePrice 
      : currentProduct.price;

    // Step 1: Get products from the same category
    if (currentProduct.category) {
      // Query products in same category, ordered by sales count (descending)
      const categoryQuery = query(
        productsCollection,
        where('disabled', '!=', true),
        where('price', '>', 0),
        where('category', '==', currentProduct.category),
        orderBy('disabled'),
        orderBy('price'),
        orderBy('salesCount', 'desc'),
        orderBy('__name__'),
        limit(50) // Get more to filter by price and sort by proximity
      );

      const categorySnapshot = await getDocs(categoryQuery);
      
      const categoryProducts = categorySnapshot.docs
        .map(processProductData)
        .filter(product => {
          if (product.id === productId || product.disabled || product.price <= 0) {
            return false;
          }
          
          // Calculate product's effective price
          const productPrice = product.onSale && product.onSalePrice 
            ? product.onSalePrice 
            : product.price;
          
          // Check if product is within $2 price range
          const priceDifference = Math.abs(productPrice - currentPrice);
          return priceDifference <= 2;
        })
        .map(product => {
          // Add price proximity score for sorting
          const productPrice = product.onSale && product.onSalePrice 
            ? product.onSalePrice 
            : product.price;
          const priceDifference = Math.abs(productPrice - currentPrice);
          
          return {
            ...product,
            priceProximity: 2 - priceDifference // Closer = higher score
          };
        })
        .sort((a, b) => {
          // Sort by price proximity first (closer prices first)
          if (a.priceProximity !== b.priceProximity) {
            return b.priceProximity - a.priceProximity;
          }
          // Then by sales count (higher sales first)
          return (b.salesCount || 0) - (a.salesCount || 0);
        });

      relatedProducts = categoryProducts;
    }

    // Step 2: If we don't have enough products from same category, add products from other categories within $2 range
    if (relatedProducts.length < limitCount) {
      const otherCategoriesQuery = query(
        productsCollection,
        where('disabled', '!=', true),
        where('price', '>', 0),
        orderBy('disabled'),
        orderBy('price'),
        orderBy('salesCount', 'desc'),
        orderBy('__name__'),
        limit(30)
      );
      
      const otherSnapshot = await getDocs(otherCategoriesQuery);
      
      const additionalProducts = otherSnapshot.docs
        .map(processProductData)
        .filter(product => {
          if (product.id === productId || product.disabled || product.price <= 0) {
            return false;
          }
          
          // Skip if already in related products
          if (relatedProducts.some(rp => rp.id === product.id)) {
            return false;
          }
          
          // Check if product is within $2 price range
          const productPrice = product.onSale && product.onSalePrice 
            ? product.onSalePrice 
            : product.price;
          const priceDifference = Math.abs(productPrice - currentPrice);
          return priceDifference <= 2;
        })
        .map(product => {
          // Add price proximity score for sorting
          const productPrice = product.onSale && product.onSalePrice 
            ? product.onSalePrice 
            : product.price;
          const priceDifference = Math.abs(productPrice - currentPrice);
          
          return {
            ...product,
            priceProximity: 2 - priceDifference
          };
        })
        .sort((a, b) => {
          // Sort by price proximity first
          if (a.priceProximity !== b.priceProximity) {
            return b.priceProximity - a.priceProximity;
          }
          // Then by sales count
          return (b.salesCount || 0) - (a.salesCount || 0);
        });

      relatedProducts = [...relatedProducts, ...additionalProducts];
    }

    // Step 3: If still not enough products, add same-category products with broader price range
    if (relatedProducts.length < limitCount && currentProduct.category) {
      const sameCategoryBroaderQuery = query(
        productsCollection,
        where('disabled', '!=', true),
        where('price', '>', 0),
        where('category', '==', currentProduct.category),
        orderBy('disabled'),
        orderBy('price'),
        orderBy('salesCount', 'desc'),
        orderBy('__name__'),
        limit(30)
      );

      const broaderSnapshot = await getDocs(sameCategoryBroaderQuery);
      
      const broaderProducts = broaderSnapshot.docs
        .map(processProductData)
        .filter(product => {
          if (product.id === productId || product.disabled || product.price <= 0) {
            return false;
          }
          
          // Skip if already in related products
          if (relatedProducts.some(rp => rp.id === product.id)) {
            return false;
          }
          
          return true; // Accept all other products in same category
        })
        .sort((a, b) => (b.salesCount || 0) - (a.salesCount || 0));

      relatedProducts = [...relatedProducts, ...broaderProducts];
    }

    // Step 4: If still not enough, add any products with broader price range
    if (relatedProducts.length < limitCount) {
      const anyProductsQuery = query(
        productsCollection,
        where('disabled', '!=', true),
        where('price', '>', 0),
        orderBy('disabled'),
        orderBy('price'),
        orderBy('salesCount', 'desc'),
        orderBy('__name__'),
        limit(30)
      );

      const anySnapshot = await getDocs(anyProductsQuery);
      
      const anyProducts = anySnapshot.docs
        .map(processProductData)
        .filter(product => {
          if (product.id === productId || product.disabled || product.price <= 0) {
            return false;
          }
          
          // Skip if already in related products
          if (relatedProducts.some(rp => rp.id === product.id)) {
            return false;
          }
          
          return true;
        })
        .sort((a, b) => (b.salesCount || 0) - (a.salesCount || 0));

      relatedProducts = [...relatedProducts, ...anyProducts];
    }

    // Step 5: Clean up the priceProximity field and limit results
    const cleanedProducts = relatedProducts
      .map((product: any) => {
        const { priceProximity, ...cleanProduct } = product;
        return cleanProduct as Product;
      })
      .slice(0, limitCount);

    return cleanedProducts;
    
  } catch (error) {
    console.error('Error fetching related products:', error);
    
    // Fallback: return products with basic filtering
    try {
      const productsCollection = collection(db, 'products');
      const fallbackQuery = query(
        productsCollection,
        where('disabled', '!=', true),
        where('price', '>', 0),
        orderBy('disabled'),
        orderBy('price'),
        orderBy('salesCount', 'desc'),
        orderBy('__name__'),
        limit(limitCount + 5)
      );
      
      const fallbackSnapshot = await getDocs(fallbackQuery);
      
      return fallbackSnapshot.docs
        .map(processProductData)
        .filter(product => product.id !== productId && !product.disabled && product.price > 0)
        .slice(0, limitCount);
    } catch (fallbackError) {
      console.error('Error in fallback query:', fallbackError);
      return [];
    }
  }
};

/**
 * Fetch best selling products for cart recommendations
 * Excludes products already in cart and prioritizes by sales count
 */
export const getCartRecommendations = async (
  cartProductIds: string[],
  limitCount: number = 20
): Promise<Product[]> => {
  try {
    const productsCollection = collection(db, 'products');
    
    // Query for best selling products, ordered by sales count descending
    const recommendationsQuery = query(
      productsCollection,
      where('disabled', '!=', true),
      where('price', '>', 0),
      orderBy('disabled'),
      orderBy('price'),
      orderBy('salesCount', 'desc'),
      orderBy('__name__'),
      limit(limitCount + cartProductIds.length) // Get extra to filter out cart items
    );

    const snapshot = await getDocs(recommendationsQuery);
    
    const recommendations = snapshot.docs
      .map(processProductData)
      .filter(product => 
        !cartProductIds.includes(product.id) && // Exclude cart items
        !product.disabled && 
        product.price > 0
      )
      .sort((a, b) => (b.salesCount || 0) - (a.salesCount || 0)) // Ensure proper sales count ordering
      .slice(0, limitCount);

    return recommendations;
  } catch (error) {
    console.error('Error fetching cart recommendations:', error);
    throw new Error('Failed to fetch cart recommendations');
  }
};

/**
 * Check if specific products are disabled (for cart validation)
 */
export const checkProductsAvailability = async (productIds: string[]): Promise<{
  available: string[];
  disabled: { id: string; name: string }[];
}> => {
  try {
    const productsCollection = collection(db, 'products');
    const result = {
      available: [] as string[],
      disabled: [] as { id: string; name: string }[]
    };
    
    // Fetch all products by IDs
    const productPromises = productIds.map(async (id) => {
      try {
        const productRef = doc(productsCollection, id);
        const productDoc = await getDoc(productRef);
        
        if (productDoc.exists()) {
          const product = processProductData(productDoc);
          
          if (product.disabled) {
            result.disabled.push({
              id: product.id,
              name: product.name
            });
          } else {
            result.available.push(product.id);
          }
        } else {
          // Product doesn't exist - treat as disabled
          result.disabled.push({
            id: id,
            name: 'Unknown Product'
          });
        }
      } catch (error) {
        console.error(`Error fetching product ${id}:`, error);
        // On error, treat as disabled for safety
        result.disabled.push({
          id: id,
          name: 'Unknown Product'
        });
      }
    });
    
    await Promise.all(productPromises);
    
    return result;
  } catch (error) {
    console.error('Error checking product availability:', error);
    throw new Error('Failed to check product availability');
  }
}; 
